import React, { useState, useEffect } from "react";
import { Download, Share, PlusSquare, Smartphone } from "lucide-react";
import { Modal } from "@/components/ui-bits";

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallPWA({ variant = "solid", className = "" }) {
  const [canInstall, setCanInstall] = useState(!!window.__deferredInstallPrompt);
  const [showHelp, setShowHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onAvail = () => setCanInstall(true);
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
    };
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onAvail);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    const deferred = window.__deferredInstallPrompt;
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch (e) {
        console.error("Error en el prompt de instalación:", e);
      }
      window.__deferredInstallPrompt = null;
      setCanInstall(false);
    } else {
      // iOS / navegadores sin prompt nativo: mostrar instrucciones
      setShowHelp(true);
    }
  };

  const base =
    variant === "ghost"
      ? "flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-sm text-[#2E6CA4] hover:bg-[#E8F0F8] transition-colors"
      : "flex items-center justify-center gap-2 w-full py-3 px-4 rounded-full font-semibold transition-colors";

  const solidStyle =
    variant === "solid"
      ? { background: "#1F4E78", color: "#fff" }
      : {};

  return (
    <>
      <button
        onClick={handleClick}
        data-testid="install-pwa-btn"
        className={`${base} ${className}`}
        style={solidStyle}
        title="Instalar aplicación"
      >
        <Download size={variant === "ghost" ? 16 : 18} />
        {variant === "ghost" ? "Instalar app" : "Instalar app en tu teléfono"}
      </button>

      <Modal open={showHelp} onClose={() => setShowHelp(false)} title="Instalar la app" testid="install-help-modal">
        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            Agrega esta app a tu pantalla de inicio para abrirla como una aplicación normal,
            sin buscar el enlace cada vez.
          </p>
          {isIOS() ? (
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#E8F0F8] text-[#2E6CA4] flex items-center justify-center font-semibold shrink-0">1</span>
                <span>En Safari, toca el botón <strong>Compartir</strong> <Share size={15} className="inline -mt-0.5" /> (abajo).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#E8F0F8] text-[#2E6CA4] flex items-center justify-center font-semibold shrink-0">2</span>
                <span>Elige <strong>“Agregar a inicio”</strong> <PlusSquare size={15} className="inline -mt-0.5" />.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#E8F0F8] text-[#2E6CA4] flex items-center justify-center font-semibold shrink-0">3</span>
                <span>Toca <strong>“Agregar”</strong>. ¡Listo, aparecerá el ícono en tu pantalla!</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#E8F0F8] text-[#2E6CA4] flex items-center justify-center font-semibold shrink-0">1</span>
                <span>Abre el menú del navegador (⋮ o ⋯, arriba a la derecha).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#E8F0F8] text-[#2E6CA4] flex items-center justify-center font-semibold shrink-0">2</span>
                <span>Elige <strong>“Instalar app”</strong> o <strong>“Agregar a pantalla de inicio”</strong> <Smartphone size={15} className="inline -mt-0.5" />.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[#E8F0F8] text-[#2E6CA4] flex items-center justify-center font-semibold shrink-0">3</span>
                <span>Confirma. El ícono quedará en tu teléfono o escritorio.</span>
              </li>
            </ol>
          )}
        </div>
      </Modal>
    </>
  );
}
